import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { graphPostVoid } from '../api/graphClient';
import {
  Project, Task, Issue, Risk, Milestone, Budget, Expense,
} from '../types';
import { getProjectTasks } from './taskService';
import { getProjectIssues } from './issueService';
import { getProjectRisks } from './riskService';
import { getProjectMilestones } from './milestoneService';
import { getProjectBudgets, getProjectExpenses } from './budgetService';
import { getProjectMembers } from './projectMemberService';

// ── Picklist values (verified via EntityDefinitions on prod) ──────────
export const ReportType = {
  WeeklyStatus:        100000000,
  SprintReview:        100000001,
  ExecutivePortfolio:  100000002,
  MonthlyClient:       100000003,
} as const;
export const REPORT_TYPE_LABEL: Record<number, string> = {
  [ReportType.WeeklyStatus]:       'Weekly Status',
  [ReportType.SprintReview]:       'Sprint Review',
  [ReportType.ExecutivePortfolio]: 'Executive Portfolio',
  [ReportType.MonthlyClient]:      'Monthly Client',
};

export type ReportSection = 'tasks' | 'issues' | 'risks' | 'milestones' | 'budget' | 'team' | 'notes';
export const SECTION_LABEL: Record<ReportSection, string> = {
  tasks: 'Task Summary', issues: 'Issues', risks: 'Risks', milestones: 'Milestones',
  budget: 'Budget', team: 'Team', notes: 'Custom Notes',
};

export interface ReportRecipient { id?: string; name: string; email?: string | null }
export interface ReportSectionsConfig { sections: ReportSection[]; notes?: string }

export interface ReportTemplate {
  dagrun_pm_reporttemplateid: string;
  dagrun_pm_name: string;
  dagrun_pm_reporttype: number | null;
  dagrun_pm_sectionsjson?: string | null;
  dagrun_pm_recipientlistjson?: string | null;
  dagrun_pm_lastgeneratedon?: string | null;
  dagrun_pm_lastsenton?: string | null;
  _dagrun_pm_projectid_value?: string | null;
}

const FIELDS = [
  'dagrun_pm_reporttemplateid', 'dagrun_pm_name', 'dagrun_pm_reporttype',
  'dagrun_pm_sectionsjson', 'dagrun_pm_recipientlistjson',
  'dagrun_pm_lastgeneratedon', 'dagrun_pm_lastsenton', '_dagrun_pm_projectid_value',
];

export async function getProjectReportTemplates(pca: IPublicClientApplication, projectId: string): Promise<ReportTemplate[]> {
  return dvList<ReportTemplate>(pca, 'dagrun_pm_reporttemplates', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'createdon desc',
    top: 100,
  });
}

export interface CreateTemplateInput {
  projectId: string;
  name: string;
  reportType: number;
  sections: ReportSection[];
  notes?: string;
  recipients: ReportRecipient[];
}

export async function createReportTemplate(pca: IPublicClientApplication, input: CreateTemplateInput): Promise<string> {
  const sectionsConfig: ReportSectionsConfig = { sections: input.sections, notes: input.notes };
  return dvCreate(pca, 'dagrun_pm_reporttemplates', {
    dagrun_pm_name: input.name,
    dagrun_pm_reporttype: input.reportType,
    dagrun_pm_sectionsjson: JSON.stringify(sectionsConfig),
    dagrun_pm_recipientlistjson: JSON.stringify(input.recipients),
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
  });
}

export async function updateReportTemplate(
  pca: IPublicClientApplication, id: string,
  input: { name: string; reportType: number; sections: ReportSection[]; notes?: string; recipients: ReportRecipient[] },
): Promise<void> {
  const sectionsConfig: ReportSectionsConfig = { sections: input.sections, notes: input.notes };
  return dvUpdate(pca, 'dagrun_pm_reporttemplates', id, {
    dagrun_pm_name: input.name,
    dagrun_pm_reporttype: input.reportType,
    dagrun_pm_sectionsjson: JSON.stringify(sectionsConfig),
    dagrun_pm_recipientlistjson: JSON.stringify(input.recipients),
  });
}

export async function deleteReportTemplate(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_reporttemplates', id);
}

export async function markGenerated(pca: IPublicClientApplication, id: string): Promise<string> {
  const now = new Date().toISOString();
  await dvUpdate(pca, 'dagrun_pm_reporttemplates', id, { dagrun_pm_lastgeneratedon: now });
  return now;
}
export async function markSent(pca: IPublicClientApplication, id: string): Promise<string> {
  const now = new Date().toISOString();
  await dvUpdate(pca, 'dagrun_pm_reporttemplates', id, { dagrun_pm_lastsenton: now });
  return now;
}

export function parseSections(json?: string | null): ReportSectionsConfig {
  if (!json) return { sections: ['tasks', 'issues', 'risks', 'milestones'] };
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return { sections: parsed as ReportSection[] };
    return { sections: parsed.sections ?? [], notes: parsed.notes };
  } catch {
    return { sections: [] };
  }
}
export function parseRecipients(json?: string | null): ReportRecipient[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

// ── Report generation ─────────────────────────────────────────────────

const TASK_DONE_STATUS = 100000003;

function esc(s: string): string {
  return s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

/**
 * Assemble report HTML from live project data for the included sections.
 * Returns a self-contained HTML string suitable for preview and for the
 * body of a Graph sendMail call.
 */
export async function generateReportHtml(
  pca: IPublicClientApplication,
  project: Project,
  config: ReportSectionsConfig,
): Promise<string> {
  const sections = config.sections;
  const needTasks = sections.includes('tasks');
  const [tasks, issues, risks, milestones, budgets, expenses, members] = await Promise.all([
    needTasks || true ? getProjectTasks(pca, project.dagrun_pm_projectid).catch(() => [] as Task[]) : Promise.resolve([] as Task[]),
    sections.includes('issues') ? getProjectIssues(pca, project.dagrun_pm_projectid).catch(() => [] as Issue[]) : Promise.resolve([] as Issue[]),
    sections.includes('risks') ? getProjectRisks(pca, project.dagrun_pm_projectid).catch(() => [] as Risk[]) : Promise.resolve([] as Risk[]),
    sections.includes('milestones') ? getProjectMilestones(pca, project.dagrun_pm_projectid).catch(() => [] as Milestone[]) : Promise.resolve([] as Milestone[]),
    sections.includes('budget') ? getProjectBudgets(pca, project.dagrun_pm_projectid).catch(() => [] as Budget[]) : Promise.resolve([] as Budget[]),
    sections.includes('budget') ? getProjectExpenses(pca, project.dagrun_pm_projectid).catch(() => [] as Expense[]) : Promise.resolve([] as Expense[]),
    sections.includes('team') ? getProjectMembers(pca, project.dagrun_pm_projectid).catch(() => []) : Promise.resolve([]),
  ]);

  const parts: string[] = [];
  parts.push(`<div style="font-family:Segoe UI,Arial,sans-serif;color:#0F1117;max-width:760px">`);
  parts.push(`<h1 style="color:#0E7490;font-size:22px;margin:0 0 4px">${esc(project.dagrun_pm_name)}</h1>`);
  parts.push(`<div style="color:#5A6272;font-size:13px;margin-bottom:18px">Status report · generated ${new Date().toLocaleString()}</div>`);

  if (sections.includes('tasks')) {
    const total = tasks.length;
    const done = tasks.filter(t => t.dagrun_pm_status === TASK_DONE_STATUS).length;
    const pct = total > 0
      ? Math.round(tasks.reduce((a, t) => a + (t.dagrun_pm_status === TASK_DONE_STATUS ? 100 : (t.dagrun_pm_percentcomplete ?? 0)), 0) / total)
      : 0;
    parts.push(section('Task Summary', `
      <table style="${TBL}">
        <tr><td style="${TD}">Total tasks</td><td style="${TDN}">${total}</td></tr>
        <tr><td style="${TD}">Completed</td><td style="${TDN}">${done}</td></tr>
        <tr><td style="${TD}">Open</td><td style="${TDN}">${total - done}</td></tr>
        <tr><td style="${TD}">Average completion</td><td style="${TDN}">${pct}%</td></tr>
      </table>`));
  }
  if (sections.includes('issues')) {
    // "Open" = not yet resolved (resolvedon empty) — avoids hardcoding
    // status picklist values.
    const open = issues.filter(i => !i.dagrun_pm_resolvedon).length;
    parts.push(section('Issues', `
      <table style="${TBL}">
        <tr><td style="${TD}">Total issues</td><td style="${TDN}">${issues.length}</td></tr>
        <tr><td style="${TD}">Open issues</td><td style="${TDN}">${open}</td></tr>
      </table>`));
  }
  if (sections.includes('risks')) {
    parts.push(section('Risks', `
      <table style="${TBL}">
        <tr><td style="${TD}">Total risks</td><td style="${TDN}">${risks.length}</td></tr>
      </table>`));
  }
  if (sections.includes('milestones')) {
    const upcoming = milestones
      .filter(m => m.dagrun_pm_duedate)
      .sort((a, b) => new Date(a.dagrun_pm_duedate!).getTime() - new Date(b.dagrun_pm_duedate!).getTime());
    const next = upcoming.find(m => new Date(m.dagrun_pm_duedate!) >= new Date());
    const rows = upcoming.slice(0, 8).map(m =>
      `<tr><td style="${TD}">${esc(m.dagrun_pm_name)}</td><td style="${TDN}">${m.dagrun_pm_duedate ? new Date(m.dagrun_pm_duedate).toLocaleDateString() : '—'}</td></tr>`).join('');
    parts.push(section('Milestones', `
      <div style="font-size:13px;color:#5A6272;margin-bottom:6px">Next due: <strong>${next ? esc(next.dagrun_pm_name) + ' — ' + new Date(next.dagrun_pm_duedate!).toLocaleDateString() : 'none scheduled'}</strong></div>
      <table style="${TBL}">${rows || `<tr><td style="${TD}">No milestones</td><td style="${TDN}"></td></tr>`}</table>`));
  }
  if (sections.includes('budget')) {
    const allocated = budgets.reduce((a, b) => a + (b.dagrun_pm_allocatedamount ?? 0), 0);
    const spent = expenses.reduce((a, e) => a + (e.dagrun_pm_amount ?? 0), 0);
    parts.push(section('Budget', `
      <table style="${TBL}">
        <tr><td style="${TD}">Allocated</td><td style="${TDN}">${allocated.toLocaleString()}</td></tr>
        <tr><td style="${TD}">Spent (expenses)</td><td style="${TDN}">${spent.toLocaleString()}</td></tr>
        <tr><td style="${TD}">Remaining</td><td style="${TDN}">${(allocated - spent).toLocaleString()}</td></tr>
      </table>`));
  }
  if (sections.includes('team')) {
    const rows = members.map(m => `<tr><td style="${TD}">${esc(m.dagrun_pm_username ?? m.dagrun_pm_userid ?? '—')}</td></tr>`).join('');
    parts.push(section('Team', `<table style="${TBL}">${rows || `<tr><td style="${TD}">No members listed</td></tr>`}</table>`));
  }
  if (sections.includes('notes') && config.notes && config.notes.trim()) {
    parts.push(section('Notes', `<div style="font-size:13px;color:#2C313D;white-space:pre-wrap">${esc(config.notes)}</div>`));
  }

  parts.push(`</div>`);
  return parts.join('\n');
}

const TBL = 'border-collapse:collapse;width:100%;margin-bottom:6px';
const TD = 'padding:6px 10px;border-bottom:1px solid #EAECF0;font-size:13px;color:#2C313D';
const TDN = 'padding:6px 10px;border-bottom:1px solid #EAECF0;font-size:13px;color:#0F1117;text-align:right;font-weight:600';
function section(title: string, inner: string): string {
  return `<div style="margin:0 0 18px"><h2 style="font-size:15px;color:#0E7490;border-bottom:2px solid #D4EEF5;padding-bottom:4px;margin:0 0 8px">${esc(title)}</h2>${inner}</div>`;
}

/** Send the generated report HTML via Graph /me/sendMail. */
export async function sendReportEmail(
  pca: IPublicClientApplication,
  recipients: ReportRecipient[],
  subject: string,
  html: string,
): Promise<void> {
  const toRecipients = recipients
    .map(r => r.email)
    .filter((e): e is string => !!e && e.includes('@'))
    .map(address => ({ emailAddress: { address } }));
  if (toRecipients.length === 0) throw new Error('No recipients have an email address.');
  await graphPostVoid(pca, '/me/sendMail', {
    message: { subject, body: { contentType: 'HTML', content: html }, toRecipients },
    saveToSentItems: true,
  });
}
